import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { useParams } from 'react-router';
import { API } from 'aws-amplify';

import { editTextReset } from 'src/store/modules/editText';
import { buttonsReset } from 'src/store/modules/buttons';
import { imageReset } from 'src/store/modules/imageUpload';
import { pagesReset, basicFullRoulettePage } from 'src/store/modules/pages';
import { trackingReset } from 'src/store/modules/trackings';

import setInitValue from 'src/utils/setInitValue';
import { changeEnter } from 'src/utils/changeEnter';
import { useRoulette } from 'src/hooks/useRoulette';
import { getFontURL } from 'src/utils/getFontUrl';
import { initTrackingValues } from '../TemplateTracking/BasicFullRouletteTracking';

const initViewValues = {
  bg: 'color',
  bgImage: {
    result_image: 'https://image.inappmessage.com/sample/defaultGRAYIMAGE.webp'
  },
  bgOpacity: { bgDimmed: 30 },
  deeplink: { result_button: 'https://www.inappmessage.com/' },
  radius: { result_button: 12, result_image: 100, result_modal_bg: 20 },

  text: {
    title: 'SPIN & WIN',
    sub_title: 'Try your luck and win amazing rewards!',
    result_text: 'CHECK YOUR REWARD',
    result_sub_text: 'Tap below to see what you got!',
    roulette_items: [
      'Prize A',
      'Prize B',
      'Prize C',
      'Prize D',
      'Prize E',
      'Prize F'
    ]
  },
  fontSize: {
    title: 2.5,
    sub_title: 1.125,
    result_text: 1.75,
    result_sub_text: 1,
    roulette_items: 0.875
  },
  fontWeight: {
    title: 700,
    sub_title: 500,
    result_text: 700,
    result_sub_text: 400,
    roulette_items: 600
  },
  color: {
    title: '#FF6B35',
    sub_title: '#FF6B35',
    result_text: '#FF6B35',
    result_sub_text: '#666666',
    roulette_items: '#1A1A2E'
  },
  bgColor: {
    result_modal_bg: '#FFFFFF',
    roulette_items: '#FFFFFF'
  },
  fontFamily: {
    title: 'Poppins',
    sub_title: 'Poppins',
    result_text: 'Poppins',
    result_sub_text: 'Poppins',
    roulette_items: 'Poppins'
  },
  textAlign: {
    title: 'center',
    sub_title: 'center',
    result_text: 'center',
    result_sub_text: 'center',
    roulette_items: 'center'
  },

  btnText: { result_button: 'CLAIM NOW', start_button: 'SPIN' },
  btnFontSize: { result_button: 1.125, start_button: 1.125 },
  btnFontWeight: { result_button: 700, start_button: 700 },
  btnColor: { result_button: '#FFFFFF', start_button: '#FFFFFF' },
  btnBgColorR: { result_button: 255, start_button: 255 },
  btnBgColorG: { result_button: 107, start_button: 107 },
  btnBgColorB: { result_button: 53, start_button: 53 },
  btnBgColorA: { result_button: 1, start_button: 1 },
  btnFontFamily: { result_button: 'Poppins', start_button: 'Poppins' }
};

const BasicFullRoulette: FC = () => {
  const dispatch = useDispatch();

  const bgImage = useSelector((state: RootState) => state.imageUpload.bgImage);
  const bgOpacity = useSelector(
    (state: RootState) => state.imageUpload.bgImageBlur
  );
  const radius = useSelector((state: RootState) => state.imageUpload.radius);
  const bgColor = useSelector((state: RootState) => state.imageUpload.bgColor);

  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );
  const textAlign = useSelector((state: RootState) => state.editText.textAlign);

  const btnText = useSelector((state: RootState) => state.buttons.btnText);
  const btnFontSize = useSelector(
    (state: RootState) => state.buttons.btnFontSize
  );
  const btnFontWeight = useSelector(
    (state: RootState) => state.buttons.btnFontWeight
  );
  const btnColor = useSelector((state: RootState) => state.buttons.btnColor);
  const btnBgColorR = useSelector(
    (state: RootState) => state.buttons.btnBgColorR
  );
  const btnBgColorG = useSelector(
    (state: RootState) => state.buttons.btnBgColorG
  );
  const btnBgColorB = useSelector(
    (state: RootState) => state.buttons.btnBgColorB
  );
  const btnBgColorA = useSelector(
    (state: RootState) => state.buttons.btnBgColorA
  );
  const btnFontFamily = useSelector(
    (state: RootState) => state.buttons.btnFontFamily
  );

  const currentPage = useSelector(
    (state: RootState) => state.pages.basicFullRoulettePage
  );

  const { id } = useParams<{ id: string }>();
  useEffect(() => {
    // 초기값 먼저 설정
    setInitValue(dispatch, { ...initViewValues, ...initTrackingValues });

    // ID가 있으면 customValues 로드
    async function onLoad() {
      try {
        if (id) {
          const { customValues } = await API.get(
            'iam-editor',
            `/templates/${id}`,
            ''
          );
          if (customValues) {
            setInitValue(dispatch, customValues);
          }
        }
      } catch (error) {}
    }
    onLoad();

    return () => {
      dispatch(imageReset());
      dispatch(editTextReset());
      dispatch(buttonsReset());
      dispatch(pagesReset());
      dispatch(trackingReset());
    };
  }, [dispatch, id]);

  // 룰렛 아이템 동적 생성
  const rouletteItems = text?.roulette_items || [
    'coupon 10%',
    'coupon 20%',
    'coupon 30%',
    'coupon 40%',
    'coupon 50%',
    'coupon 60%'
  ];
  const probability = Math.floor(100 / rouletteItems.length);

  // 중앙 버튼 색상 계산 (기본값: #FF6B6B)
  const centerButtonColor = `rgba(${btnBgColorR?.start_button ??
    255}, ${btnBgColorG?.start_button ?? 107}, ${btnBgColorB?.start_button ??
    107}, ${btnBgColorA?.start_button ?? 1})`;
  const centerButtonTextColor = btnColor?.start_button || '#ffffff';

  // 커스텀 폰트인 경우 @font-face로 정의된 이름 사용
  const resolvedStartButtonFontFamily = [
    'Roboto',
    'Inter',
    'Montserrat',
    'Poppins',
    'Pretendard'
  ].includes(btnFontFamily?.start_button)
    ? btnFontFamily?.start_button
    : 'startBtnFont';

  const resolvedRouletteItemsFontFamily = [
    'Roboto',
    'Inter',
    'Montserrat',
    'Poppins',
    'Pretendard'
  ].includes(fontFamily?.roulette_items)
    ? fontFamily?.roulette_items
    : 'rouletteItemsFont';

  const { containerRef, canvasRef, closeModal } = useRoulette({
    items: rouletteItems.map(name => ({
      name,
      probability
    })),
    borderColor: centerButtonColor,
    centerButtonColor,
    centerButtonTextColor,
    centerButtonText: btnText?.start_button || 'START',
    centerButtonFontSize: btnFontSize?.start_button || 1.125,
    centerButtonFontWeight: btnFontWeight?.start_button || 700,
    centerButtonFontFamily: resolvedStartButtonFontFamily || 'Roboto',
    sectorColor: bgColor?.roulette_items || '#D5D5D5',
    itemTextColor: color?.roulette_items || '#000000',
    itemFontSize: fontSize?.roulette_items || 0.75,
    itemFontWeight: fontWeight?.roulette_items || 400,
    itemFontFamily: resolvedRouletteItemsFontFamily || 'Roboto',
    onSpinEnd: () => {
      dispatch(basicFullRoulettePage(1));
    }
  });

  // 룰렛 완료 후 결과 화면으로 자동 전환

  const backgroundDimmed = bgOpacity?.bgDimmed / 100;
  const displayValue = backgroundDimmed >= 0.01 ? backgroundDimmed : 0.7;

  const titleFontURL = getFontURL(fontFamily?.title);
  const subTitleFontURL = getFontURL(fontFamily?.sub_title);
  const btnFontURL = getFontURL(btnFontFamily?.result_button);
  const startBtnFontURL = getFontURL(btnFontFamily?.start_button);
  const rouletteItemsFontURL = getFontURL(fontFamily?.roulette_items);
  const resultTextFontURL = getFontURL(fontFamily?.result_text);
  const resultSubTextFontURL = getFontURL(fontFamily?.result_sub_text);

  const style = `
    @font-face {
      font-family: 'titleFont';
      src: url(${titleFontURL});
    }
    @font-face {
      font-family: 'subTitleFont';
      src: url(${subTitleFontURL});
    }
    @font-face {
      font-family: 'btnFont';
      src: url(${btnFontURL});
    }
    @font-face {
      font-family: 'startBtnFont';
      src: url(${startBtnFontURL});
    }
    @font-face {
      font-family: 'rouletteItemsFont';
      src: url(${rouletteItemsFontURL});
    }
    @font-face {
      font-family: 'resultTextFont';
      src: url(${resultTextFontURL});
    }
    @font-face {
      font-family: 'resultSubTextFont';
      src: url(${resultSubTextFontURL});
    }

    .root-view {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, ${displayValue});
    }

    canvas {
      width: 350px;
      height: 350px;
      background-color: transparent;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      -webkit-user-select: none;
      border: none;
    }

    .roulette-container {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      max-width: 400px;
      height: 100%;
      padding: 20px;
      z-index: 1;
    }

    .header {
      position: relative;
      display: flex;
      flex-direction: column;
      width: 100%;
      height: auto;
      margin-bottom: 20px;
      gap: 5px;
    }

    .title {
      width: 100%;
      height: auto;
      max-height: 48px;
      line-height: 1.2;
      font-size: ${fontSize?.title || 2.25}rem;
      font-weight: ${fontWeight?.title || 700};
      color: ${color?.title || '#ffffff'};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.title
        )
          ? fontFamily?.title
          : 'titleFont'
      }, sans-serif;
      text-align: ${textAlign?.title || 'center'};
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      display: -webkit-box;
    }

    .sub-title {
      width: 100%;
      height: auto;
      min-height: 80px;
      line-height: 1.4;
      font-size: ${fontSize?.sub_title || 1.125}rem;
      font-weight: ${fontWeight?.sub_title || 400};
      color: ${color?.sub_title || '#ffffff'};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.sub_title
        )
          ? fontFamily?.sub_title
          : 'subTitleFont'
      }, sans-serif;
      text-align: ${textAlign?.sub_title || 'center'};
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      display: -webkit-box;
      overflow: hidden;
    }

    .close-btn {
      position: absolute;
      top: 6%;
      right: 10%;
      background: none;
      border: none;
      color: #ffffff;
      width: 24px;
      height: 24px;
      font-size: 1.5rem;
      cursor: pointer;
      z-index: 10;
    }

    .roulette-wrapper {
      position: relative;
      display: flex;
      justify-content: center;
      width: 100%;
      height: fit-content;
    }

    .pin {
      position: absolute;
      top: -15px;
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-top: 20px solid #ffffff;
      z-index: 1;
    }

    @keyframes fadeInBackground {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes slideUpModal {
      from {
        opacity: 0;
        transform: translateY(30px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .modal-background {
      position: absolute;
      top: 0;
      left: 0;
      display: none;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, ${displayValue});
      z-index: 2;
      padding: 0 20px;
    }

    .modal-background.show {
      animation: fadeInBackground 0.6s ease-out;
    }

    .modal-content {
      width: 100%;
      max-width: 340px;
      padding: 26px 20px 20px 20px;
      display: flex;
      flex-direction: column;
      gap: 1rem;
      border-radius: ${radius?.result_modal_bg}px;
      background-color: ${bgColor?.result_modal_bg};
    }

    .modal-background.show .modal-content {
      animation: slideUpModal 0.6s ease-out;
    }

    .result-text {
      width: 100%;
      height: auto;
      line-height: 1.4;
      font-size: ${fontSize?.result_text}rem;
      font-weight: ${fontWeight?.result_text};
      color: ${color?.result_text};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.result_text
        )
          ? fontFamily?.result_text
          : 'resultTextFont'
      }, sans-serif;
      text-align: ${textAlign?.result_text};
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      display: -webkit-box;
      overflow: hidden;
      margin-bottom: 7px;
    }

    .result-sub-text {
      width: 100%;
      height: auto;
      line-height: 1.54;
      font-size: ${fontSize?.result_sub_text}rem;
      font-weight: ${fontWeight?.result_sub_text};
      color: ${color?.result_sub_text};
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          fontFamily?.result_sub_text
        )
          ? fontFamily?.result_sub_text
          : 'resultSubTextFont'
      }, sans-serif;
      text-align: ${textAlign?.result_sub_text};
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      display: -webkit-box;
      overflow: hidden;
    }

    .result-icon {
      width: 100%;
      text-align: center;
      margin: 10px 0;
    }

    .result-image {
      width: 224px;
      height: 224px;
      border-radius: ${radius?.result_image}px;
      object-fit: cover;
    }

    .result-btn {
      width: 100%;
      height: 48px;
      outline: 0;
      border: 0;
      padding: 0 16px;
      margin-top: 4px;

      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      cursor: pointer;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      font-weight: ${btnFontWeight?.result_button};
      font-size: ${btnFontSize?.result_button}rem;
      font-family: ${
        ['Roboto', 'Inter', 'Montserrat', 'Poppins', 'Pretendard'].includes(
          btnFontFamily?.result_button
        )
          ? btnFontFamily?.result_button
          : 'btnFont'
      };

      color: ${btnColor?.result_button};
      border-radius: ${radius?.result_button}px;
      background-color:rgba(${btnBgColorR?.result_button},${
    btnBgColorG?.result_button
  },
        ${btnBgColorB?.result_button}, ${btnBgColorA?.result_button});
    }

    .step-tabs {
      display: flex;
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 10;
      background: rgba(255, 255, 255, 0.9);
      border-radius: 8px;
      overflow: hidden;
    }

    .step-tab {
      padding: 8px 16px;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      border: none;
      background: transparent;
      color: #666;
      transition: all 0.2s;
    }

    .step-tab.active {
      background: #000;
      color: #fff;
    }

    .step-tab:hover:not(.active) {
      background: rgba(0, 0, 0, 0.1);
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: style }} />
      <div className="root-view">
        <div className="step-tabs">
          <button
            className={`step-tab ${currentPage === 0 ? 'active' : ''}`}
            onClick={() => {
              if (currentPage !== 0) {
                closeModal();
              }
              dispatch(basicFullRoulettePage(0));
            }}
          >
            Start Screen
          </button>
          <button
            className={`step-tab ${currentPage === 1 ? 'active' : ''}`}
            onClick={() => dispatch(basicFullRoulettePage(1))}
          >
            Result Screen
          </button>
        </div>

        <div className="roulette-container" ref={containerRef}>
          <div className="close-btn">✕</div>
          {currentPage === 0 && (
            <>
              <div className="header">
                <div className="title">{changeEnter(text?.title)}</div>
                <div className="sub-title">{changeEnter(text?.sub_title)}</div>
              </div>

              <div className="roulette-wrapper" key="roulette-page">
                <div className="pin" />
                <canvas ref={canvasRef} id="rouletteCanvas" />
              </div>
            </>
          )}

          {currentPage === 1 && (
            <div className="modal-background show" style={{ display: 'flex' }}>
              <div className="modal-content">
                <div className="result-text">
                  {changeEnter(text?.result_text)}
                </div>
                <div className="result-sub-text">
                  {changeEnter(text?.result_sub_text)}
                </div>
                <div className="result-icon">
                  {bgImage?.result_image && (
                    <img
                      src={bgImage.result_image}
                      alt="result"
                      className="result-image"
                    />
                  )}
                </div>
                <div
                  className="result-btn"
                  onClick={() => {
                    closeModal();
                    dispatch(basicFullRoulettePage(0));
                  }}
                >
                  {btnText?.result_button}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BasicFullRoulette;
